import { useEffect, useState } from 'react';
import { Lock } from 'lucide-react';
import {
  TENANCY_STATUSES,
  formatAmountInput,
  formatDate,
  formatEuro,
  parseAmountInput,
  tenancyStatusLabels,
  type TenancyRecord,
  type TenancyStatus,
} from '@brimo/shared';
import { Banner, Button, Dialog, Icon, Select, TextField, Textarea, useToast } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { createTenancy, deleteTenancy, updateTenancy } from '../api';
import { CardHead, DashedButton, DocumentRows, FieldPair, StatusPill } from '../bits';
import { OwnerUploadDialog } from '../record/OwnerUploadDialog';
import type { TenancyBody, Tone, UnitDetail } from '../types';
import { splitTenancies, tenancyDocumentItem, tenancyDocuments } from './unitData';
import styles from './cards.module.css';
import dialogStyles from '../record/dialogs.module.css';

const statusTones: Record<TenancyStatus, Tone> = {
  active: 'success',
  terminated: 'warning',
  ended: 'neutral',
};

const monthly = (cents: number | null): string => (cents === null ? '—' : `${formatEuro(cents)} mtl.`);
const day = (value: string | null): string => (value ? formatDate(value) : '—');

/**
 * „Mietverhältnis" (D-188): the active tenancy with its documents (Mietvertrag, Mieterhöhungen, … — owner `tenancy`,
 * D-202), the history and the create/edit dialog.
 */
export function TenancyCard({
  unit,
  editable,
  onChanged,
}: {
  unit: UnitDetail;
  editable: boolean;
  onChanged: () => void;
}) {
  const { toast } = useToast();
  const [dialog, setDialog] = useState<{ item: TenancyRecord | null } | null>(null);
  const [upload, setUpload] = useState<{ tenancyId: string; documentTypeId: string | undefined } | null>(
    null,
  );
  const { active, history } = splitTenancies(unit.tenancies);
  const activeDocuments = active ? tenancyDocuments(unit.tenancyDocuments, active.id) : [];
  const openSlot = activeDocuments.find((row) => row.fileId === null && row.status === 'missing');
  const importOnly = unit.external?.mode === 'import_only';
  const lockedOf = (item: TenancyRecord) => item.imported && importOnly;

  return (
    <section className={styles.card} aria-labelledby="unit-tenancy">
      <CardHead
        eyebrow="Mietverhältnis"
        id="unit-tenancy"
        end={
          editable && !active ? (
            <Button variant="ghost" size="small" onClick={() => setDialog({ item: null })}>
              Mietverhältnis anlegen
            </Button>
          ) : undefined
        }
      />
      {active ? (
        <div className={styles.tenancy}>
          <div className={styles.tenancyTop}>
            <span className={styles.tenancyName}>{active.tenantName ?? 'Mieter nicht angegeben'}</span>
            <StatusPill tone={statusTones[active.status]}>{active.statusLabel}</StatusPill>
          </div>
          <div className={styles.tenancyGrid}>
            <FieldPair label="Beginn" value={day(active.beginOn)} />
            <FieldPair label="Kaltmiete" value={monthly(active.rentColdCents)} />
            <FieldPair label="Nebenkosten" value={monthly(active.serviceChargeCents)} />
            <FieldPair label="Heizkosten" value={monthly(active.heatingCostCents)} />
            <FieldPair
              label="Kaution"
              value={active.depositCents === null ? '—' : formatEuro(active.depositCents)}
            />
            <FieldPair label="Ende" value={day(active.endOn)} />
          </div>
          {active.notes ? <p className={styles.rowMeta}>{active.notes}</p> : null}
          <div className={styles.subhead}>Unterlagen zum Mietverhältnis</div>
          <DocumentRows
            items={activeDocuments.map(tenancyDocumentItem)}
            emptyText="Noch keine Unterlagen zum Mietverhältnis."
          />
          {editable ? (
            <DashedButton
              onClick={() =>
                setUpload({ tenancyId: active.id, documentTypeId: openSlot?.documentType?.id ?? undefined })
              }
            >
              {openSlot ? 'Mietvertrag hochladen' : 'Unterlage hochladen'}
            </DashedButton>
          ) : null}
          {editable ? (
            <div className={styles.addAction}>
              {lockedOf(active) ? (
                <span className={styles.lock}>
                  <Icon icon={Lock} size={16} />
                  Wird aus TeamProQ übernommen
                </span>
              ) : (
                <Button variant="secondary" size="small" onClick={() => setDialog({ item: active })}>
                  Bearbeiten
                </Button>
              )}
            </div>
          ) : null}
        </div>
      ) : (
        <p className={styles.empty}>Kein Mietverhältnis hinterlegt.</p>
      )}
      {history.length > 0 ? (
        <>
          <div className={styles.subhead}>Frühere Mietverhältnisse</div>
          <div className={styles.rows}>
            {history.map((item) => (
              <div key={item.id} className={styles.row}>
                <div className={styles.rowMain}>
                  <span className={styles.rowLabel}>{item.tenantName ?? 'Mieter nicht angegeben'}</span>
                  <span className={styles.rowMeta}>
                    {day(item.beginOn)} – {day(item.endOn)} · {monthly(item.rentColdCents)}
                  </span>
                  <DocumentRows
                    items={tenancyDocuments(unit.tenancyDocuments, item.id).map(tenancyDocumentItem)}
                  />
                </div>
                <StatusPill tone={statusTones[item.status]}>{item.statusLabel}</StatusPill>
                {editable && !lockedOf(item) ? (
                  <Button variant="ghost" size="small" onClick={() => setDialog({ item })}>
                    Bearbeiten
                  </Button>
                ) : null}
              </div>
            ))}
          </div>
        </>
      ) : null}
      <OwnerUploadDialog
        open={upload !== null}
        owner={{ tenancyId: upload?.tenancyId ?? '' }}
        eyebrow="Mietverhältnis"
        title="Unterlage zum Mietverhältnis hochladen"
        documentTypeId={upload?.documentTypeId}
        onClose={() => setUpload(null)}
        onUploaded={(row) => {
          setUpload(null);
          toast({ message: `Hochgeladen: ${row.name}. Die Datei wird geprüft.` });
          onChanged();
        }}
      />
      {dialog ? (
        <TenancyDialog
          unitId={unit.id}
          item={dialog.item}
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null);
            toast({ message: 'Mietverhältnis gespeichert.' });
            onChanged();
          }}
          onDeleted={() => {
            setDialog(null);
            toast({ message: 'Mietverhältnis entfernt.' });
            onChanged();
          }}
        />
      ) : null}
    </section>
  );
}

interface Draft {
  status: TenancyStatus;
  tenantName: string;
  tenantEmail: string;
  tenantPhone: string;
  beginOn: string;
  endOn: string;
  rentColdCents: string;
  serviceChargeCents: string;
  heatingCostCents: string;
  depositCents: string;
  notes: string;
}

const money = (cents: number | null | undefined): string =>
  cents === null || cents === undefined ? '' : formatAmountInput(cents);

function draftOf(item: TenancyRecord | null): Draft {
  return {
    status: item?.status ?? 'active',
    tenantName: item?.tenantName ?? '',
    tenantEmail: item?.tenantEmail ?? '',
    tenantPhone: item?.tenantPhone ?? '',
    beginOn: item?.beginOn ?? '',
    endOn: item?.endOn ?? '',
    rentColdCents: money(item?.rentColdCents),
    serviceChargeCents: money(item?.serviceChargeCents),
    heatingCostCents: money(item?.heatingCostCents),
    depositCents: money(item?.depositCents),
    notes: item?.notes ?? '',
  };
}

const text = (value: string): string | null => (value.trim() === '' ? null : value.trim());

function TenancyDialog({
  unitId,
  item,
  onClose,
  onSaved,
  onDeleted,
}: {
  unitId: string;
  item: TenancyRecord | null;
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const [draft, setDraft] = useState<Draft>(() => draftOf(item));
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setDraft(draftOf(item));
    setFields({});
    setError(null);
    setConfirmDelete(false);
  }, [item]);

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  async function submit() {
    const errors: Record<string, string> = {};
    const amount = (value: string, key: string): number | null => {
      if (value.trim() === '') return null;
      const parsed = parseAmountInput(value);
      if (parsed === null) errors[key] = 'Bitte einen Betrag angeben.';
      return parsed;
    };
    const body: TenancyBody = {
      status: draft.status,
      tenantName: text(draft.tenantName),
      tenantEmail: text(draft.tenantEmail),
      tenantPhone: text(draft.tenantPhone),
      beginOn: text(draft.beginOn),
      endOn: text(draft.endOn),
      rentColdCents: amount(draft.rentColdCents, 'rentColdCents'),
      serviceChargeCents: amount(draft.serviceChargeCents, 'serviceChargeCents'),
      heatingCostCents: amount(draft.heatingCostCents, 'heatingCostCents'),
      depositCents: amount(draft.depositCents, 'depositCents'),
      notes: text(draft.notes),
    };
    if (draft.status !== 'active' && !body.endOn)
      errors['endOn'] = 'Bei gekündigten oder beendeten Mietverhältnissen bitte das Ende angeben.';
    if (Object.keys(errors).length > 0) {
      setFields(errors);
      return;
    }
    setFields({});
    setError(null);
    setPending(true);
    try {
      if (item) await updateTenancy(unitId, item.id, body);
      else await createTenancy(unitId, body);
      onSaved();
    } catch (failure) {
      if (failure instanceof ApiError && Object.keys(failure.fields).length > 0) setFields(failure.fields);
      else setError(errorMessage(failure));
    } finally {
      setPending(false);
    }
  }

  async function remove() {
    if (!item) return;
    setPending(true);
    setError(null);
    try {
      await deleteTenancy(unitId, item.id);
      onDeleted();
    } catch (failure) {
      setError(errorMessage(failure));
      setPending(false);
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      eyebrow="Mietverhältnis"
      title={item ? 'Mietverhältnis bearbeiten' : 'Mietverhältnis anlegen'}
      subtitle="Ein laufendes Mietverhältnis markiert die Einheit als vermietet."
      width={680}
      footer={
        <>
          {item && !item.imported ? (
            confirmDelete ? (
              <Button variant="destructive" loading={pending} onClick={() => void remove()}>
                Endgültig entfernen
              </Button>
            ) : (
              <Button variant="ghost" onClick={() => setConfirmDelete(true)} disabled={pending}>
                Entfernen
              </Button>
            )
          ) : null}
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button loading={pending && !confirmDelete} onClick={() => void submit()}>
            Speichern
          </Button>
        </>
      }
    >
      <div className={dialogStyles.form}>
        {error ? <Banner tone="danger">{error}</Banner> : null}
        <div className={dialogStyles.group}>
          <div className={dialogStyles.groupTitle}>Mieter</div>
          <TextField
            label="Name"
            value={draft.tenantName}
            error={fields['tenantName']}
            onChange={(event) => set('tenantName', event.target.value)}
          />
          <div className={dialogStyles.grid2}>
            <TextField
              label="E-Mail"
              type="email"
              value={draft.tenantEmail}
              error={fields['tenantEmail']}
              onChange={(event) => set('tenantEmail', event.target.value)}
            />
            <TextField
              label="Telefon"
              type="tel"
              value={draft.tenantPhone}
              error={fields['tenantPhone']}
              onChange={(event) => set('tenantPhone', event.target.value)}
            />
          </div>
        </div>
        <div className={dialogStyles.group}>
          <div className={dialogStyles.groupTitle}>Vertrag</div>
          <div className={dialogStyles.grid3}>
            <Select
              label="Status"
              value={draft.status}
              options={TENANCY_STATUSES.map((key) => ({ value: key, label: tenancyStatusLabels[key] }))}
              onChange={(event) => set('status', event.target.value as TenancyStatus)}
            />
            <TextField
              label="Beginn"
              type="date"
              value={draft.beginOn}
              error={fields['beginOn']}
              onChange={(event) => set('beginOn', event.target.value)}
            />
            <TextField
              label="Ende"
              type="date"
              value={draft.endOn}
              error={fields['endOn']}
              onChange={(event) => set('endOn', event.target.value)}
            />
          </div>
          <div className={dialogStyles.grid2}>
            <TextField
              label="Kaltmiete mtl."
              type="money"
              value={draft.rentColdCents}
              error={fields['rentColdCents']}
              onChange={(event) => set('rentColdCents', event.target.value)}
            />
            <TextField
              label="Nebenkosten mtl."
              type="money"
              value={draft.serviceChargeCents}
              error={fields['serviceChargeCents']}
              onChange={(event) => set('serviceChargeCents', event.target.value)}
            />
            <TextField
              label="Heizkosten mtl."
              type="money"
              value={draft.heatingCostCents}
              error={fields['heatingCostCents']}
              onChange={(event) => set('heatingCostCents', event.target.value)}
            />
            <TextField
              label="Kaution"
              type="money"
              value={draft.depositCents}
              error={fields['depositCents']}
              onChange={(event) => set('depositCents', event.target.value)}
            />
          </div>
          <Textarea
            label="Notiz"
            optional
            value={draft.notes}
            onChange={(event) => set('notes', event.target.value)}
          />
        </div>
        {confirmDelete ? (
          <p className={dialogStyles.warningBox}>
            Das Mietverhältnis wird aus der Akte entfernt. Diese Aktion lässt sich nicht rückgängig machen.
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
